import React, { useState, useEffect, useContext } from 'react';
import { Button, Card, CardBody, Modal, Input, Label, FormGroup, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import Error from 'pages/NotificationModal/Error';
import LoadingOverlay from 'react-loading-overlay';
import NotificationContext from './NotificationContext';
import EmailConfigTable from './EmailConfigTable';
import EditConfigView from './EditConfigView';

// ─── Constants ───────────────────────────────────────────────────────────────

const GROUP_BY_OPTIONS = [
    { value: 'none',                label: 'No Grouping' },
    { value: 'customer_id',         label: 'Customer ID' },
    { value: 'Revenue_code',        label: 'Revenue Code' },
    { value: 'location_id',         label: 'Location ID' },
    { value: 'master_location_uid', label: 'Master Location UID' },
    { value: 'customer_name',       label: 'Customer Name' },
    { value: 'csr',                 label: 'CSR' },
    { value: 'operations_user',     label: 'Operations User' },
    { value: 'appt_sys_ref',        label: 'Appointment System' },
    { value: 'appt_req_user_dept',  label: 'User Department' },
    { value: 'seg_alloc_code',      label: 'Seg Alloc Code' },
];

const BASE_RULE_EXPRS = {
    customer_id:         'ord_cust_id',
    Revenue_code:        "PATHVAL('ext_attrbs.ordRevCode')",
    location_id:         't_loc_id',
    master_location_uid: 'm_loc_uid',
    customer_name:       'ord_cust_nm',
    csr:                 "PATHVAL('ext_attrbs.tms_additional_attribs.CSR')",
    operations_user:     "PATHVAL('ext_attrbs.tms_additional_attribs.operationsUser')",
    appt_sys_ref:        'appt_sys_ref',
    appt_req_user_dept:  "PATHVAL('ext_attrbs.tms_additional_attribs.appt_reqstd_usr_dept')",
    seg_alloc_code:      "PATHVAL('ext_attrbs.tms_additional_attribs.segAllocCode')",
};

const NOTIF_RULE_ROWS = [
    { name: 'notif_rule_for_exp',  label: 'Exception Notifications',    notes: 'EMAIL selection rule' },
    { name: 'notif_rule_for_qcon', label: 'Confirmation Notifications', notes: 'EMAIL selection rule for confirmation notifications' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Splits top-level comma-separated args, respecting nested parens/brackets/braces
const splitTopLevelArgs = (str) => {
    const args = [];
    let depth = 0, current = '';
    for (const ch of str) {
        if ('([{'.includes(ch)) depth++;
        else if (')]}'.includes(ch)) depth--;
        else if (ch === ',' && depth === 0) { args.push(current.trim()); current = ''; continue; }
        current += ch;
    }
    if (current.trim()) args.push(current.trim());
    return args;
};

// routeByList is array of arrays: [[field1], [field2, field3], ...]
// Each inner array is one arg to EMAIL_GRP_BY_KEY; 2+ fields → f-string composite key
const buildMultiRuleExpr = (routeByList) => {
    const args = routeByList
        .map(slot => {
            if (slot.length === 1) {
                const key = slot[0];
                // Known field expression → use raw expression
                if (BASE_RULE_EXPRS[key]) return BASE_RULE_EXPRS[key];
                // Literal group name → wrap in quotes
                return `'${key}'`;
            }
            // Multiple fields in a slot → concatenation composite (field-based only)
            const fields = slot.map(r => BASE_RULE_EXPRS[r]).filter(Boolean);
            if (fields.length === 0) return null;
            if (fields.length === 1) return fields[0];
            return fields.join(" + '_' + ");
        })
        .filter(Boolean);
    if (args.length === 0) return "EMAIL_GRP_BY_KEY()";
    return `EMAIL_GRP_BY_KEY(${args.join(', ')})`;
};

// Extracts known field keys from "field1 + '_' + field2" concatenation, or null if any field is unknown
const parseConcatFields = (concatStr) => {
    const parts = concatStr.split(/\s*\+\s*'_'\s*\+\s*/);
    if (parts.length < 2) return null;
    const result = [];
    for (const part of parts) {
        const trimmed = part.trim();
        const match = Object.entries(BASE_RULE_EXPRS).find(([, v]) => v === trimmed);
        if (!match) return null;
        result.push(match[0]);
    }
    return result;
};

// Returns { routeByList: [[field,...], ...] } if parseable, else null
const parseExpr = (expr) => {
    if (!expr) return null;
    const trimmed = expr.trim();
    if (!trimmed.startsWith('EMAIL_GRP_BY_KEY(')) return null;
    const inner = trimmed.slice('EMAIL_GRP_BY_KEY('.length, -1);
    if (inner.includes(' if ') || inner.includes(' else ')) return null;
    const args = splitTopLevelArgs(inner);
    if (args.length === 0) return { routeByList: [] };
    const routeByList = [];
    for (const arg of args) {
        // Check for concatenation pattern: field + '_' + field
        if (arg.includes("+ '_' +")) {
            const fields = parseConcatFields(arg);
            if (!fields) return null;
            routeByList.push(fields);
        } else {
            const match = Object.entries(BASE_RULE_EXPRS).find(([, v]) => v === arg);
            if (match) {
                routeByList.push([match[0]]);
            } else if (/^['"](.+)['"]$/.test(arg)) {
                // Quoted string literal — use inner value as-is
                routeByList.push([arg.slice(1, -1)]);
            } else {
                return null;
            }
        }
    }
    return { routeByList };
};

// Returns { rule } for table display
const parseRoutingDisplay = (expr) => {
    if (!expr) return { rule: 'Not set' };
    const parsed = parseExpr(expr);
    if (!parsed) return {
        rule: <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#495057', wordBreak: 'break-all' }}>{expr}</span>
    };
    if (parsed.routeByList.length === 0) return { rule: 'Not set' };
    return { rule: (
        <div className="d-flex flex-wrap align-items-center gap-1">
            {parsed.routeByList.map((slot, i) => {
                const label = slot.map(r => GROUP_BY_OPTIONS.find(o => o.value === r)?.label || r).join(' _ ');
                return (
                    <React.Fragment key={i}>
                        {i > 0 && <i className="fas fa-chevron-right" style={{ fontSize: '0.5rem', color: '#ced4da' }}></i>}
                        <span style={{
                            backgroundColor: '#e8f4fd', color: '#1971c2',
                            border: '1px solid #bde0fb', borderRadius: '12px',
                            fontSize: '0.7rem', fontWeight: '500', padding: '2px 8px',
                        }}>{label}</span>
                    </React.Fragment>
                );
            })}
        </div>
    ) };
};

// ─── Shared UI Components ─────────────────────────────────────────────────────

const InfoTooltip = ({ text }) => (
    <span style={{ position: 'relative', display: 'inline-block', marginLeft: '6px', cursor: 'help' }} className="info-tooltip-wrapper">
        <i className="fas fa-info-circle" style={{ fontSize: '0.7rem', color: '#adb5bd' }}></i>
        <span className="info-tooltip-text" style={{
            visibility: 'hidden', opacity: 0,
            position: 'absolute', bottom: '120%', left: '50%', transform: 'translateX(-50%)',
            backgroundColor: '#343a40', color: '#fff',
            padding: '10px 14px', borderRadius: '6px',
            fontSize: '0.75rem', fontWeight: '400', lineHeight: '1.5',
            width: '300px', textTransform: 'none', letterSpacing: 'normal',
            whiteSpace: 'normal', zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            transition: 'opacity 0.2s',
        }}>{text}</span>
        <style>{`
            .info-tooltip-wrapper:hover .info-tooltip-text {
                visibility: visible !important;
                opacity: 1 !important;
            }
        `}</style>
    </span>
);

const SectionHeader = ({ icon, title, action, tooltip, step }) => (
    <div className="d-flex justify-content-between align-items-center mb-3" style={{ borderBottom: '2px solid #f4f6f8', paddingBottom: '10px' }}>
        <span style={{ fontSize: '0.7rem', fontWeight: '700', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {step && (
                <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: '18px', height: '18px', borderRadius: '50%',
                    backgroundColor: '#4263eb', color: '#fff',
                    fontSize: '0.6rem', fontWeight: '700', marginRight: '8px',
                }}>{step}</span>
            )}
            <i className={`fas fa-${icon} me-2`}></i>{title}
            {tooltip && <InfoTooltip text={tooltip} />}
        </span>
        {action}
    </div>
);

const EmailChip = ({ email, onRemove }) => {
    const isMacro = email.startsWith('@') && email.endsWith('@');
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            backgroundColor: isMacro ? '#e8f4fd' : '#f1f3f5',
            color: isMacro ? '#1971c2' : '#495057',
            border: `1px solid ${isMacro ? '#bde0fb' : '#dee2e6'}`,
            borderRadius: '20px', fontSize: '0.72rem', fontWeight: '500', padding: '2px 10px',
        }}>
            {email}
            {onRemove && (
                <i className="fas fa-times" style={{ cursor: 'pointer', fontSize: '9px', opacity: 0.6 }} onClick={onRemove}></i>
            )}
        </span>
    );
};

const JsonSection = ({ config }) => {
    const [show, setShow] = React.useState(false);
    return (
        <div className='mt-4'>
            <div className='d-flex align-items-center gap-2 mb-2' style={{ cursor: 'pointer' }} onClick={() => setShow(!show)}>
                <h6 className='mb-0 text-secondary'>Expand for JSON</h6>
                <i className={`fas fa-caret-${show ? 'up' : 'down'} text-secondary`} style={{ fontSize: '15px' }}></i>
            </div>
            {show && (
                <div style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '5px', padding: '15px', fontFamily: 'monospace', fontSize: '0.875rem', maxHeight: '400px', overflowY: 'auto' }}>
                    <pre>{JSON.stringify(config, null, 2)}</pre>
                </div>
            )}
        </div>
    );
};

// Field-based options that can be combined into composite keys (not literals like DEFAULT)
const COMBINABLE_FIELDS = new Set(Object.keys(BASE_RULE_EXPRS));

// routeByList: [[group,...], ...]  — each slot is one arg to EMAIL_GRP_BY_KEY
const RoutingPriorityList = ({ routeByList, onChange, options }) => {
    const dragIndex = React.useRef(null);
    const [dragOver, setDragOver] = React.useState(null);
    const [combineSlot, setCombineSlot] = React.useState(null); // index of slot being combined

    const selected = new Set(routeByList.flat());

    // Fields available to combine into a given slot (exclude already-used fields)
    const getCombineOptions = (slotIndex) => {
        const slotKeys = new Set(routeByList[slotIndex]);
        return options.filter(o =>
            COMBINABLE_FIELDS.has(o.value) && !selected.has(o.value) && !slotKeys.has(o.value)
        );
    };

    // Can this slot accept a combine? Only if it already contains field-based keys
    const canCombine = (slot) => slot.every(key => COMBINABLE_FIELDS.has(key));

    return (
        <>
            {options.length > 0 && (
                <div className="mb-3">
                    <Input type="select" style={{ fontSize: '0.85rem' }} value=""
                        onChange={e => { if (e.target.value) onChange([...routeByList, [e.target.value]]); }}>
                        <option value="">+ Add group...</option>
                        {options.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </Input>
                </div>
            )}

            {routeByList.length > 1 && (
                <div style={{ fontSize: '0.72rem', color: '#868e96', marginBottom: '6px' }}>
                    <i className="fas fa-grip-vertical me-1" style={{ fontSize: '0.6rem' }}></i>
                    Drag to change priority — groups higher in the list are matched first.
                </div>
            )}

            {routeByList.length > 0 && (
                <div style={{ border: '1px solid #e9ecef', borderRadius: '6px', overflow: 'hidden', marginBottom: '12px' }}>
                    {routeByList.map((slot, i) => {
                        const combineOptions = getCombineOptions(i);
                        return (
                        <div key={i} draggable
                            onDragStart={() => { dragIndex.current = i; }}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(i); }}
                            onDragLeave={() => setDragOver(null)}
                            onDrop={() => {
                                setDragOver(null);
                                if (dragIndex.current === null || dragIndex.current === i) return;
                                const l = [...routeByList];
                                const [moved] = l.splice(dragIndex.current, 1);
                                l.splice(i, 0, moved);
                                onChange(l);
                                dragIndex.current = null;
                            }}
                            style={{
                                borderBottom: i < routeByList.length - 1 ? '1px solid #f1f3f5' : 'none',
                                backgroundColor: dragOver === i ? '#e8f4fd' : '#fff',
                                padding: '8px 12px', userSelect: 'none',
                            }}>
                            <div className="d-flex align-items-center gap-2">
                                <i className="fas fa-grip-vertical" style={{ fontSize: '0.7rem', color: '#ced4da', cursor: 'grab', flexShrink: 0 }}></i>
                                <span style={{ fontSize: '0.7rem', color: '#adb5bd', width: '16px', flexShrink: 0 }}>{i + 1}</span>
                                <div className="d-flex flex-wrap align-items-center gap-1" style={{ flex: 1 }}>
                                    {slot.map((key, fi) => {
                                        const label = options.find(o => o.value === key)?.label || key;
                                        return (
                                            <React.Fragment key={fi}>
                                                {fi > 0 && <span style={{ fontSize: '0.7rem', color: '#adb5bd' }}>_</span>}
                                                <span style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                    backgroundColor: '#e8f4fd', color: '#1971c2',
                                                    border: '1px solid #bde0fb', borderRadius: '12px',
                                                    fontSize: '0.72rem', fontWeight: '500', padding: '2px 8px',
                                                }}>
                                                    {label}
                                                    <i className="fas fa-times" style={{ fontSize: '9px', cursor: 'pointer', opacity: 0.6 }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const newSlot = slot.filter((_, j) => j !== fi);
                                                            if (newSlot.length === 0) onChange(routeByList.filter((_, j) => j !== i));
                                                            else onChange(routeByList.map((s, j) => j === i ? newSlot : s));
                                                        }} />
                                                </span>
                                            </React.Fragment>
                                        );
                                    })}
                                    {/* Combine button — only for field-based slots with available fields */}
                                    {canCombine(slot) && combineOptions.length > 0 && (
                                        combineSlot === i ? (
                                            <Input type="select" bsSize="sm"
                                                style={{ fontSize: '0.72rem', width: 'auto', display: 'inline-block', padding: '1px 6px', height: 'auto' }}
                                                value="" autoFocus
                                                onBlur={() => setCombineSlot(null)}
                                                onChange={e => {
                                                    if (e.target.value) {
                                                        onChange(routeByList.map((s, j) => j === i ? [...s, e.target.value] : s));
                                                    }
                                                    setCombineSlot(null);
                                                }}>
                                                <option value="">select field...</option>
                                                {combineOptions.map(o => (
                                                    <option key={o.value} value={o.value}>{o.label}</option>
                                                ))}
                                            </Input>
                                        ) : (
                                            <span
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center',
                                                    backgroundColor: '#f1f3f5', color: '#868e96',
                                                    border: '1px dashed #ced4da', borderRadius: '12px',
                                                    fontSize: '0.65rem', fontWeight: '500', padding: '2px 7px',
                                                    cursor: 'pointer',
                                                }}
                                                title="Combine with another field to create a composite key"
                                                onClick={(e) => { e.stopPropagation(); setCombineSlot(i); }}>
                                                +
                                            </span>
                                        )
                                    )}
                                </div>
                                <Button color="link" size="sm" className="p-0 text-danger" style={{ fontSize: '0.7rem' }}
                                    onClick={(e) => { e.stopPropagation(); onChange(routeByList.filter((_, idx) => idx !== i)); }}>
                                    <i className="fas fa-times"></i>
                                </Button>
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}
        </>
    );
};

// ─── Component ────────────────────────────────────────────────────────────────

const EMPTY_VAR_MODAL = {
    show: false,
    editingVarName: null,
    editingVarLabel: '',
    editingVarNotes: '',
    routeByList: [],
    isAdvanced: false,
    advancedName: '',
    advancedNotes: '',
    advancedValue: '',
};

const NotificationConfig = () => {
    const {
        loading, error, setError, notificationConfig,
        loadNotificationConfig, setSelectedRule,
        updateNotificationConfig,
    } = useContext(NotificationContext);

    const [varModal, setVarModal] = useState(EMPTY_VAR_MODAL);
    const [editMode, setEditMode] = useState({ active: false, configData: null, index: null });
    const [plainEdit, setPlainEdit] = useState(null);
    const [plainInputModal, setPlainInputModal] = useState({ show: false, field: null, value: '' });

    const hasRoutingRules = notificationConfig?.vars?.some(v => v.name === 'notif_rule_for_exp');

    useEffect(() => { loadNotificationConfig(); }, []);

    const togError = () => setError(false);

    // ── var modal helpers ──

    const groupOptions = [
        { value: 'DEFAULT', label: 'DEFAULT' },
        { value: 'CUSTOMER_LEVEL_CONTACT', label: 'CUSTOMER_LEVEL_CONTACT' },
        ...GROUP_BY_OPTIONS.filter(o => o.value !== 'none'),
    ];

    const openEditNotifRule = (row) => {
        const existingVar = notificationConfig?.vars?.find(v => v.name === row.name);
        const expr = existingVar?.value;
        const parsed = parseExpr(expr);
        if (expr && !parsed) {
            // Truly unparseable (if/else logic, etc.) → expression mode
            setVarModal({
                ...EMPTY_VAR_MODAL, show: true,
                editingVarName: row.name, editingVarLabel: row.label, editingVarNotes: row.notes,
                isAdvanced: true,
                advancedValue: expr,
            });
        } else {
            setVarModal({
                ...EMPTY_VAR_MODAL, show: true,
                editingVarName: row.name, editingVarLabel: row.label, editingVarNotes: row.notes,
                routeByList: parsed?.routeByList || [],
            });
        }
    };

    const closeVarModal = () => setVarModal(EMPTY_VAR_MODAL);

    const saveVar = () => {
        const varName = varModal.editingVarName;
        const varNotes = varModal.editingVarNotes;

        let value;
        if (varModal.isAdvanced) {
            if (!(varModal.advancedValue || '').trim()) return;
            value = varModal.advancedValue.trim();
        } else {
            value = buildMultiRuleExpr(varModal.routeByList);
        }

        // Keep selectedRule in sync for exception notifications
        if (varName === 'notif_rule_for_exp') {
            const list = varModal.routeByList.flatMap(slot => slot);
            setSelectedRule(list.length > 0 ? list[0] : 'none');
        }

        const currentVars = notificationConfig?.vars || [];
        const exists = currentVars.some(v => v.name === varName);
        const updatedVars = exists
            ? currentVars.map(v => v.name === varName ? { ...v, value, notes: varNotes } : v)
            : [...currentVars, { name: varName, type: 'EXPR', notes: varNotes, value }];

        updateNotificationConfig({ ...notificationConfig, vars: updatedVars });
        closeVarModal();
    };

    const copyExceptionRuleToConfirmation = () => {
        const expVar = notificationConfig?.vars?.find(v => v.name === 'notif_rule_for_exp');
        if (!expVar) return;
        const currentVars = notificationConfig?.vars || [];
        const exists = currentVars.some(v => v.name === 'notif_rule_for_qcon');
        const qconVar = { name: 'notif_rule_for_qcon', type: 'EXPR', notes: 'EMAIL selection rule for confirmation notifications', value: expVar.value };
        const updatedVars = exists
            ? currentVars.map(v => v.name === 'notif_rule_for_qcon' ? qconVar : v)
            : [...currentVars, qconVar];
        updateNotificationConfig({ ...notificationConfig, vars: updatedVars });
    };

    // ── edit config ──

    const handleEditConfig = (configData, index) => setEditMode({ active: true, configData, index });
    const handleBackToList = () => {
        setEditMode({ active: false, configData: null, index: null });
        loadNotificationConfig();
    };

    // ── plain config helpers ──

    const PLAIN_FIELDS = [
        { key: 'to',         label: 'TO',         note: 'Direct recipients when no routing rules are set.' },
        { key: 'cc',         label: 'CC',         note: 'Copied when no routing rules are set.' },
        { key: 'default_to', label: 'Default TO', note: 'Receives forwarded emails that could not be parsed.' },
        { key: 'default_cc', label: 'Default CC', note: 'Copied on forwarded emails that could not be parsed.' },
        { key: 'file_to',    label: 'File TO',    note: 'Deprecated — was used for file update notifications.' },
        { key: 'file_cc',    label: 'File CC',    note: 'Deprecated — was used for file update notifications.' },
    ];

    const toArr = (v) => {
        if (!v) return [];
        const arr = Array.isArray(v) ? v : [v];
        return arr.flatMap(e => typeof e === 'string' ? e.split(',').map(s => s.trim()).filter(Boolean) : [e]);
    };

    const startEditPlain = () => setPlainEdit({
        to:         toArr(notificationConfig?.to),
        cc:         toArr(notificationConfig?.cc),
        default_to: toArr(notificationConfig?.default_to),
        default_cc: toArr(notificationConfig?.default_cc),
        file_to:    toArr(notificationConfig?.file_to),
        file_cc:    toArr(notificationConfig?.file_cc),
    });

    const savePlain = async () => {
        const updated = { ...notificationConfig };
        for (const { key } of PLAIN_FIELDS) {
            if (!plainEdit[key].length) delete updated[key];
            else updated[key] = plainEdit[key].join(',');
        }
        const success = await updateNotificationConfig(updated);
        if (success) setPlainEdit(null);
    };

    const addPlainEmail = () => {
        const email = plainInputModal.value.trim();
        if (!email) return;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email) || plainEdit[plainInputModal.field].includes(email)) return;
        setPlainEdit({ ...plainEdit, [plainInputModal.field]: [...plainEdit[plainInputModal.field], email] });
        setPlainInputModal({ show: false, field: null, value: '' });
    };

    const removePlainEmail = (field, index) =>
        setPlainEdit({ ...plainEdit, [field]: plainEdit[field].filter((_, i) => i !== index) });

    if (editMode.active) {
        return (
            <EditConfigView
                configData={editMode.configData}
                configIndex={editMode.index}
                onBack={handleBackToList} />
        );
    }

    return (
        <>
            <Modal isOpen={error} toggle={togError} centered size="sm">
                <Error message="Something went wrong while loading notification configuration" closeModal={togError} />
            </Modal>

            <LoadingOverlay active={loading} spinner>
                <Card style={{ border: '1px solid #e9ecef', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                    <CardBody className="p-4">

                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h6 className="mb-0 fw-bold" style={{ color: '#212529', fontSize: '0.95rem' }}>
                                <i className="fas fa-bell me-2 text-primary"></i>Notification Configuration
                            </h6>
                        </div>

                        {/* ── Section 1: Routing Rules ── */}
                        <div className="mb-4">
                            <SectionHeader icon="route" title="Routing Rules" step="1"
                                tooltip={<><b>Exception Notifications</b> — sent when something goes wrong with an order (e.g. order not found, timeslots not available).<br/><br/><b>Confirmation Notifications</b> — sent when an order is successfully confirmed.<br/><br/>Each rule picks which email groups should receive that type of notification. Groups higher in the list are matched first — drag to change priority.</>}
                            />
                            <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ color: '#6c757d', borderBottom: '1px solid #e9ecef' }}>
                                        <th style={{ padding: '6px 10px', width: '32%', fontWeight: '600' }}>Applies To</th>
                                        <th style={{ padding: '6px 10px', fontWeight: '600' }}>Routing Rule</th>
                                        <th style={{ padding: '6px 10px', width: '80px' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {NOTIF_RULE_ROWS.map(row => {
                                        const v = notificationConfig?.vars?.find(vv => vv.name === row.name);
                                        const { rule } = parseRoutingDisplay(v?.value);
                                        const isQcon = row.name === 'notif_rule_for_qcon';
                                        const expVar = notificationConfig?.vars?.find(vv => vv.name === 'notif_rule_for_exp');
                                        return (
                                            <tr key={row.name} style={{ borderBottom: '1px solid #f1f3f5' }}>
                                                <td style={{ padding: '9px 10px', color: '#495057' }}>{row.label}</td>
                                                <td style={{ padding: '9px 10px', color: v ? '#212529' : '#adb5bd', fontStyle: v ? 'normal' : 'italic' }}>
                                                    {v ? rule : 'Not set'}
                                                </td>
                                                <td style={{ padding: '9px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                    <div className="d-flex justify-content-end gap-2">
                                                        {isQcon && expVar && (
                                                            <Button size="sm" outline color="primary" style={{ fontSize: '0.75rem' }}
                                                                onClick={() => copyExceptionRuleToConfirmation()}
                                                                disabled={loading}>
                                                                Same as Exception
                                                            </Button>
                                                        )}
                                                        <Button color="link" size="sm" className="p-0 text-primary" style={{ fontSize: '0.75rem', textDecoration: 'none' }} onClick={() => openEditNotifRule(row)}>Edit</Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* ── Section 2: Email Groups ── */}
                        <div className="mb-4">
                            <SectionHeader
                                icon="layer-group"
                                title="Email Groups" step="2"
                                tooltip={<><b>Groups</b> are named sets of email addresses.<br/><br/>When an order comes in, the routing rule figures out which group it belongs to, and that group's emails get notified.<br/><br/>The <b>DEFAULT</b> group catches anything that doesn't match another group.</>}
                                action={
                                    <Button size="sm" outline color="primary" style={{ fontSize: '0.75rem' }} onClick={() => handleEditConfig({ grps: [], emails: { to: [] } }, -1)} disabled={loading}>
                                        <i className="fas fa-plus me-1"></i>Add Group
                                    </Button>
                                }
                            />
                            {!hasRoutingRules && notificationConfig?.email_cfg?.some(c => !c.grps?.includes('DEFAULT')) && (
                                <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '6px', padding: '10px 14px', marginBottom: '12px', fontSize: '0.78rem', color: '#856404' }}>
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                    <b>No routing rule configured.</b> Email groups have been added, but without a routing rule (Step 1) the system won't know which group to use. Configure a routing rule first.
                                </div>
                            )}
                            <EmailConfigTable onEditConfig={handleEditConfig} />
                        </div>

                        {/* ── Section 3: General Emails ── */}
                        {notificationConfig && (
                            <div className="mb-4">
                                <SectionHeader
                                    icon="envelope"
                                    title="General Emails"
                                    tooltip={<>These emails are only used when <b>no routing rules</b> are configured. If routing rules exist, email groups take priority instead.</>}
                                    action={!plainEdit && (
                                        <Button size="sm" outline color="secondary" style={{ fontSize: '0.75rem' }} onClick={startEditPlain}>
                                            <i className="fas fa-pen me-1"></i>Edit
                                        </Button>
                                    )}
                                />
                                {plainEdit ? (
                                    <>
                                        {PLAIN_FIELDS.map(({ key, label, note }) => (
                                            <div key={key} className="mb-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#495057' }}>{label}<InfoTooltip text={note} /></span>
                                                    <Button color="link" size="sm" className="p-0 text-success" style={{ textDecoration: 'none', fontSize: '0.75rem' }}
                                                        onClick={() => setPlainInputModal({ show: true, field: key, value: '' })}>
                                                        + Add
                                                    </Button>
                                                </div>
                                                <div className="d-flex flex-wrap gap-2">
                                                    {plainEdit[key].map((email, i) => (
                                                        <EmailChip key={i} email={email} onRemove={() => removePlainEmail(key, i)} />
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                        <div className="d-flex justify-content-end gap-2 mt-3">
                                            <Button color="secondary" size="sm" onClick={() => setPlainEdit(null)} disabled={loading}>Cancel</Button>
                                            <Button color="primary" size="sm" onClick={savePlain} disabled={loading}>{loading ? 'Saving...' : 'Save'}</Button>
                                        </div>
                                    </>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {PLAIN_FIELDS.map(({ key, label, note }) => {
                                            const emails = toArr(notificationConfig[key]);
                                            if (!emails.length) return null;
                                            return (
                                                <div key={key} className="d-flex align-items-start gap-3">
                                                    <span style={{ fontSize: '0.7rem', fontWeight: '700', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.05em', width: '100px', paddingTop: '3px', flexShrink: 0 }}>{label}<InfoTooltip text={note} /></span>
                                                    <div className="d-flex flex-wrap gap-1">
                                                        {emails.map((email, i) => <EmailChip key={i} email={email} />)}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {notificationConfig && <JsonSection config={notificationConfig} />}

                        {!notificationConfig && !loading && (
                            <div style={{ padding: '48px', textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px dashed #dee2e6' }}>
                                <i className="fas fa-bell-slash mb-3" style={{ fontSize: '36px', color: '#ced4da', display: 'block' }}></i>
                                <h6 className="text-muted mb-1">No notification configuration found</h6>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </LoadingOverlay>

            {/* ── Add / Edit Var Modal ── */}
            <Modal isOpen={varModal.show} toggle={closeVarModal} centered>
                <ModalHeader toggle={closeVarModal}>
                    {varModal.editingVarLabel || 'Edit Rule'}
                </ModalHeader>
                <ModalBody>
                    {!varModal.isAdvanced ? (
                        <>
                            <FormGroup>
                                <RoutingPriorityList
                                    routeByList={varModal.routeByList}
                                    onChange={list => setVarModal({ ...varModal, routeByList: list })}
                                    options={groupOptions}
                                />
                            </FormGroup>
                            <Button color="link" size="sm" className="p-0 mt-2 text-muted" style={{ fontSize: '0.75rem', textDecoration: 'underline' }} onClick={() => setVarModal({ ...varModal, isAdvanced: true, advancedValue: buildMultiRuleExpr(varModal.routeByList) })}>
                                Expression
                            </Button>
                        </>
                    ) : (
                        <>
                            <FormGroup>
                                <Label style={{ fontSize: '0.85rem' }}>Expression</Label>
                                <Input type="textarea" rows={4} value={varModal.advancedValue} onChange={e => setVarModal({ ...varModal, advancedValue: e.target.value })} placeholder="e.g. EMAIL_GRP_BY_KEY('DEFAULT')" style={{ fontSize: '0.82rem', fontFamily: 'monospace' }} />
                            </FormGroup>
                            <Button color="link" size="sm" className="p-0 text-muted" style={{ fontSize: '0.75rem', textDecoration: 'underline' }} onClick={() => setVarModal({ ...varModal, isAdvanced: false })}>
                                    ← Back to visual editor
                            </Button>
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={saveVar}>Save</Button>
                    <Button color="secondary" onClick={closeVarModal}>Cancel</Button>
                </ModalFooter>
            </Modal>

            {/* ── Add Plain Email Modal ── */}
            <Modal isOpen={plainInputModal.show} toggle={() => setPlainInputModal({ show: false, field: null, value: '' })} centered>
                <ModalHeader toggle={() => setPlainInputModal({ show: false, field: null, value: '' })}>Add Email</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label style={{ fontSize: '0.85rem' }}>Email Address</Label>
                        <Input
                            type="email"
                            value={plainInputModal.value}
                            onChange={e => setPlainInputModal({ ...plainInputModal, value: e.target.value })}
                            onKeyDown={e => e.key === 'Enter' && addPlainEmail()}
                            placeholder="Enter email address"
                            autoFocus
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={addPlainEmail}>OK</Button>
                    <Button color="secondary" onClick={() => setPlainInputModal({ show: false, field: null, value: '' })}>Cancel</Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default NotificationConfig;